/**
 * Text im Textlayer einer PDF-Seite wiederfinden. pdf.js legt je Zeile einen eigenen `<span>` an und
 * trennt am Zeilenende („Si-" | „cherheit"), der Abschnittstext aus der Aufbereitung hat dort ein
 * Leerzeichen — gesucht wird deshalb über Knotengrenzen hinweg und ohne Leerraum, ohne Groß/klein.
 */

const LOCALE = "de";

interface Char {
  node: Text;
  offset: number;
}

/** Zeichen des Teilbaums ohne Leerraum, mit ihrer Stelle im Textknoten. */
const charsOf = (root: Node): { hay: string; at: Char[] } => {
  const doc = root.ownerDocument ?? document;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let hay = "";
  const at: Char[] = [];
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const text = node.nodeValue ?? "";
    for (let i = 0; i < text.length; i++) {
      if (/\s/.test(text[i])) continue;
      const lower = text[i].toLocaleLowerCase(LOCALE);
      if (lower.length !== 1) continue;
      hay += lower;
      at.push({ node, offset: i });
    }
  }
  return { hay, at };
};

/** Ohne Leerraum und ohne Aufzählungszeichen am Anfang („- (3) …") — die stehen im PDF als Grafik oder fehlen. */
const squeeze = (s: string) =>
  s
    .replace(/^\s*[-–•▪·*]+\s*/, "")
    .replace(/\s+/g, "")
    .toLocaleLowerCase(LOCALE);

/**
 * Bereiche für jede Suchphrase, in Reihenfolge der Phrasen (`null`, wo nichts gefunden wurde). Lange
 * Sätze, die so nicht im Textlayer stehen (Silbentrennung ohne Bindestrich, Fußnoten dazwischen),
 * werden ersatzweise an ihrem Anfang markiert.
 */
export const findPhraseRanges = (root: Node | null, phrases: string[], fallbackChars = 60): Array<Range | null> => {
  if (!root) return phrases.map(() => null);
  const { hay, at } = charsOf(root);
  const doc = root.ownerDocument ?? document;
  const toRange = (start: number, length: number) => {
    const first = at[start];
    const last = at[start + length - 1];
    const range = doc.createRange();
    range.setStart(first.node, first.offset);
    range.setEnd(last.node, last.offset + 1);
    return range;
  };
  return phrases.map((phrase) => {
    const needle = squeeze(phrase);
    if (needle.length < 8) return null;
    const hit = hay.indexOf(needle);
    if (hit >= 0) return toRange(hit, needle.length);
    const head = needle.slice(0, fallbackChars);
    const headHit = head.length >= 20 ? hay.indexOf(head) : -1;
    return headHit >= 0 ? toRange(headHit, head.length) : null;
  });
};
